import React, { useEffect, useRef, useState } from "react";
import { DialogContainer } from "react-md";
import FontIcon from "react-md/lib/FontIcons";
import EventService from "../../../../../../service/event/EventService";
import CartDashboardUtils from "../../services/CartDashboardUtils";

const DESCRIPTION_WORD_LIMIT = 250;

const wordCount = (s) => {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
};

/**
 * "Reject order" reason picker, opened from PrescribedDiagnosticsScreen when
 * the reviewer hits the teal "Reject order" CTA on a PENDING_REVIEW cart.
 *
 * Fetches the dropdown options from GET /admin/reject-reasons on mount, lets
 * the reviewer pick one + type a free-text description, and on Continue calls
 * POST /admin/{cartId}/reject. POST body shape:
 *   { expectedHeadRevision: number, reason: number, description?: string }
 * where `reason` is the RejectReason id. (Mirrors the hold body — both the
 * catalog and the request shape were aligned with hold when reject reasons
 * moved into the reject_reasons collection.)
 *
 * An option carrying `requiresDescription: true` (e.g. "Other reason") makes
 * the description mandatory. The backend enforces this too and 400s without
 * it, so gating Continue here is what turns a generic error toast into an
 * obvious disabled button.
 *
 * `expectedHeadRevision` is an optimistic-concurrency token. The bridge does
 * not always have a fresh head revision in hand, so we send 0 first and
 * transparently retry once with the revision the backend returns inside the
 * 409 body (the head CartPaymentRequest, or `{ headRevision: 0 }` for the
 * fresh-cart path). Only a second conflict surfaces as an error.
 *
 * On success calls `onSuccess(updatedCart)` and the screen swaps this dialog
 * for RejectOrderSuccessDialog.
 */
const RejectOrderDialog = ({ visible, cartId, onClose, onSuccess }) => {
  const cartDashboardUtils = useRef(new CartDashboardUtils()).current;
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(true);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setReasonsLoading(true);
    // Active only — a retired reason must not be offered for a new rejection.
    cartDashboardUtils.fetchRejectReasons(
      false,
      (res) => {
        setReasons(Array.isArray(res) ? res : []);
        setReasonsLoading(false);
      },
      (err) => {
        setReasonsLoading(false);
        EventService.emit("showError", {
          message: `Error loading reject reasons: ${
            err?.message || "Unknown error"
          }`,
        });
      },
    );
  }, [visible]);

  useEffect(() => {
    if (visible) {
      setSelectedReason(null);
      setDescription("");
      setReasonsOpen(false);
      setSubmitting(false);
    }
  }, [visible]);

  const descWords = wordCount(description);
  const descriptionRequired = !!selectedReason?.requiresDescription;
  const descriptionMissing = descriptionRequired && description.trim() === "";
  const canContinue = !!selectedReason && !descriptionMissing && !submitting;

  // The 409 body is either the head CartPaymentRequest (has `revision`) or
  // `{ headRevision: 0 }`. Anything else is a real error.
  const extractConflictRevision = (errBody) => {
    if (errBody && typeof errBody === "object") {
      if (typeof errBody.revision === "number") return errBody.revision;
      if (typeof errBody.headRevision === "number") return errBody.headRevision;
    }
    return null;
  };

  const submitReject = (expectedHeadRevision, isRetry) => {
    cartDashboardUtils.rejectCart(
      cartId,
      {
        expectedHeadRevision,
        reason: selectedReason.id,
        description: description.trim(),
      },
      (res) => {
        setSubmitting(false);
        if (onSuccess) onSuccess(res);
      },
      (err) => {
        if (!isRetry) {
          const conflictRev = extractConflictRevision(err);
          if (conflictRev != null && conflictRev !== expectedHeadRevision) {
            submitReject(conflictRev, true);
            return;
          }
        }
        setSubmitting(false);
        EventService.emit("showError", {
          message: `Failed to reject order: ${err?.message || "Unknown error"}`,
        });
      },
    );
  };

  const handleContinue = () => {
    if (!canContinue) return;
    setSubmitting(true);
    // Optimistic first attempt; the retry path picks up the real revision if
    // the backend says we were stale.
    submitReject(0, false);
  };

  return (
    <DialogContainer
      id="cart-reject-order-dialog"
      visible={visible}
      onHide={onClose}
      focusOnMount={false}
      width={"560px"}
      autosizeContent={true}
      dialogStyle={{ borderRadius: "10px" }}
      modal
    >
      <div className="mhd-container">
        <div className="mhd-header">
          <h2 className="mhd-title">Reject order</h2>
          <span className="mhd-close-btn" onClick={onClose}>
            &times;
          </span>
        </div>

        <div className="mhd-body">
          <div className="mhd-label">Reason for rejection</div>
          <div className="mhd-select-wrap">
            <button
              type="button"
              className={`mhd-select ${reasonsOpen ? "mhd-select--open" : ""} ${
                !selectedReason ? "mhd-select--placeholder" : ""
              }`}
              onClick={() => !reasonsLoading && setReasonsOpen((v) => !v)}
              disabled={reasonsLoading || submitting}
            >
              <span>
                {reasonsLoading
                  ? "Loading…"
                  : selectedReason
                    ? selectedReason.label
                    : "Select reason"}
              </span>
              <FontIcon style={{ fontSize: "20px", color: "#f97f0f" }}>
                {reasonsOpen ? "expand_less" : "expand_more"}
              </FontIcon>
            </button>

            {reasonsOpen && (
              <div className="mhd-select-menu">
                {reasons.length === 0 ? (
                  <div className="mhd-select-empty">No reasons available</div>
                ) : (
                  reasons.map((r) => (
                    <div
                      key={r.id}
                      className={`mhd-select-option ${
                        selectedReason?.id === r.id
                          ? "mhd-select-option--selected"
                          : ""
                      }`}
                      onClick={() => {
                        setSelectedReason(r);
                        setReasonsOpen(false);
                      }}
                    >
                      {r.label}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="mhd-label" style={{ marginTop: "20px" }}>
            Please describe the reason for rejection
            {descriptionRequired && <span className="mhd-required"> *</span>}
          </div>
          <div className="mhd-textarea-wrap">
            <textarea
              className="mhd-textarea"
              placeholder={
                descriptionRequired
                  ? "Required for the selected reason"
                  : "Type here…"
              }
              value={description}
              onChange={(e) => {
                const next = e.target.value;
                if (wordCount(next) <= DESCRIPTION_WORD_LIMIT) {
                  setDescription(next);
                }
              }}
              disabled={submitting}
              rows={3}
            />
            <div className="mhd-textarea-count">
              {descWords}/{DESCRIPTION_WORD_LIMIT} words
            </div>
          </div>
        </div>

        <div className="mhd-footer">
          <button
            type="button"
            className="mhd-btn mhd-btn--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Go back
          </button>
          <button
            type="button"
            className={`mhd-btn mhd-btn--primary ${
              !canContinue ? "mhd-btn--disabled" : ""
            }`}
            onClick={handleContinue}
            disabled={!canContinue}
          >
            {submitting ? "Rejecting…" : "Continue"}
          </button>
        </div>
      </div>
    </DialogContainer>
  );
};

export default RejectOrderDialog;
